import { createApi } from '@reduxjs/toolkit/query/react';
import { VITE_API_V2_URL } from 'codes';
import { type IShareholder } from 'components/Map/Sections/Compliance/CreditReport/types';
import { axiosBaseQuery } from 'helpers/axiosbaseQuery';
import type {
  CreditLimit,
  MapSummary,
  RiskCategories,
  TradePresentation,
} from 'types';
import type {
  AccountComment,
  Approval,
  ApproveMapModule,
  BuyerFinancialReportInsightsResponse,
  ComplianceApprovals,
  CreditCommitteeApprovals,
  DealFactsheetResponse,
  IEvaluation,
  IncomlendProduct,
  IncomlendSubProduct,
  InvestmentCard,
  Logo,
  MapRiskParamValues,
  MapTemplate,
  MapTemplate_V2,
  MapTemplatesDisplay,
  Versions,
} from './api-maps.d';

export type MAP_TYPE = 'background' | 'financial' | 'transactions';
export type MAP_USE_CASE = 'background' | 'financial' | 'transactions';
export type MAP_PARTY = 'buyer' | 'supplier' | 'relationship';

export const maps = createApi({
  reducerPath: 'maps',
  tagTypes: [
    'TEMPLATES',
    'TABLE_VALUES',
    'TRANSACTION_TABLE_VALUES',
    'SUMMARY_TABLE_VALUES',
    'SCORE_CARD',
    'MAP_DOCUMENTS',
    'INSURERS',
    'ACCOUNT_COMMENTS',
    'RELATIONSHIP_COMMENTS',
    'SCORECARD_HEADER',
    'SCORE_CARD_PDF',
    'SHAREHOLDERS',
    'EVALUATION',
    'SCORE_CARD_PDF',
    'RISK_MAP_APPROVERS',
    'COMPLIANCE_MAP_APPROVERS',
    'LEGAL_MAP_APPROVALS',
    'CREDIT_COMMITEE_APPROVERS',
    'CURRENT_MAP_VERSION',
    'CURRENT_TEMPLATE_VERSION',
    'MAP_ITEM_VERSIONS_LIST',
    'CURRENT_TEMPLATE_VERSIONS',
    'CURRENT_TEMPLATE_ITEM_VERSIONS',
    'TRADE_PRESENTATION',
    'MAP_DOCUMENTS',
    'MAP_PHASE_STATUS',
    'MAP_EXTENTION',
    'MAP_LIST',
    'MAP_INSTALLMENT',
  ],
  baseQuery: axiosBaseQuery({
    baseUrl: `${VITE_API_V2_URL}/maps`,
  }),
  endpoints: (builder) => ({
    getMaps: builder.query<any, { mapparams: string }>({
      query: ({ mapparams }) => ({ url: `${mapparams}`, method: 'get' }),
      transformResponse: (response) => response?.data,
      providesTags: ['MAP_LIST'],
    }),

    getMapVersion: builder.query<Versions, string>({
      //zhe
      query: (map_name) => ({
        url: `/${map_name}/versions`,
        method: 'get',
      }),
      transformResponse: (response) => response?.data,
      providesTags: ['CURRENT_MAP_VERSION'],
    }),
    getTemplateVersions: builder.query<Versions, null>({
      //zhe
      query: () => ({
        url: `/template/versions`,
        method: 'get',
      }),
      transformResponse: (response) => response?.data,
      providesTags: ['CURRENT_TEMPLATE_VERSIONS'],
    }),
    getTemplateVersion: builder.query<Versions, null>({
      //zhe
      query: () => ({
        url: `/template/versions`,
        method: 'get',
      }),
      transformResponse: (response) => response?.data,
      providesTags: ['CURRENT_TEMPLATE_VERSION'],
    }),
    getCurrentMapItemTemplates: builder.query<MapTemplatesDisplay, string>({
      query: (version) => ({
        url: `/template/versions/${version}`,
        method: 'get',
      }),
      transformResponse: (response) => response?.data,
      providesTags: ['CURRENT_TEMPLATE_ITEM_VERSIONS'],
    }),
    getMapItemTemplates: builder.query<MapTemplatesDisplay, string>({
      query: (version) => ({
        url: `/template/versions/${version}`,
        method: 'get',
      }),
      transformResponse: (response) => response?.data,
    }),
    getTemplates: builder.query<
      Record<string, string>[],
      {
        party: MAP_PARTY;
        use_case: MAP_USE_CASE;
        ipl_product: string;
      }
    >({
      query: ({ party, use_case, ipl_product }) => ({
        url: `/template/${use_case}/${ipl_product}/${party}/versions`,
        method: 'get',
      }),
      transformResponse: (response) => response?.data,
      providesTags: ['MAP_ITEM_VERSIONS_LIST'],
    }),
    getTemplatesV2: builder.query<
      MapTemplate_V2,
      {
        party: MAP_PARTY;
        use_case: MAP_USE_CASE;
        ipl_product: string;
        map_version?: string;
      }
    >({
      //zhe
      query: ({ party, use_case, ipl_product, map_version }) => ({
        url: `/template/${use_case}/${ipl_product}/${party}${map_version ? `?map_version=${map_version}` : ''}`,
        method: 'get',
      }),
      transformResponse: (response) => response?.data,
      providesTags: ['TEMPLATES'],
    }),
    getMapItemTemplateByVersion: builder.query<
      MapTemplate,
      {
        item_name: string;
        version: string;
      }
    >({
      query: ({ item_name, version }) => ({
        url: `/template/${item_name}/${version}`,
        method: 'get',
      }),
      transformResponse: (response) => response?.data,
    }),
    editMapRiskParameters: builder.mutation<
      any,
      {
        map_name: string;
        account_type: 'supplier' | 'buyer';
        data: {
          map_metadata_id: string;
          source: string;
          value: any;
        };
      }
    >({
      query: ({ map_name, account_type, data }) => {
        return {
          url: `/${map_name}/${account_type}`,
          method: 'PATCH',
          data,
        };
      },
      transformResponse: (response) => response?.data,
      invalidatesTags: ['TABLE_VALUES'],
      async onQueryStarted(arg, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          dispatch(maps.util.invalidateTags(['TABLE_VALUES']));
          dispatch(maps.util.invalidateTags(['EVALUATION']));
        } catch (error) {
          //eslint-disable-next-line no-console
          console.error('Error deleting director:', error);
        }
      },
    }),
    getMapRiskParameters: builder.query<
      MapRiskParamValues,
      {
        map_name: string;
        account_type: 'supplier' | 'buyer';
        item_type: MAP_TYPE;
      }
    >({
      query: ({ map_name, account_type, item_type }) => ({
        url: `/${map_name}/${account_type}/${item_type}`,
        method: 'get',
      }),
      transformResponse: (response) => response?.data,
      providesTags: ['TABLE_VALUES'],
    }),
    getMapRiskParametersV2: builder.query<
      //zhe
      MapRiskParamValues,
      {
        map_name: string;
        party: 'supplier' | 'buyer';
        use_case: MAP_USE_CASE;
      }
    >({
      query: ({ map_name, party, use_case }) => ({
        url: `/${map_name}/${party}/${use_case}`,
        method: 'get',
      }),
      transformResponse: (response) => response?.data,
      providesTags: ['TABLE_VALUES'],
    }),
    getMapTransactionValues: builder.query<
      MapRiskParamValues,
      {
        map_name: string;
      }
    >({
      query: ({ map_name }) => ({
        url: `/${map_name}/relationship/transactions`,
        method: 'get',
      }),
      transformResponse: (response) => response?.data,
      providesTags: ['TRANSACTION_TABLE_VALUES'],
    }),

    editMapTransactionDetails: builder.mutation<
      any,
      {
        map_name: string;
        data: {
          map_metadata_id: string;
          source: string;
          value: any;
        };
      }
    >({
      query: ({ map_name, data }) => {
        return {
          url: `/${map_name}/relationship`,
          method: 'PATCH',
          data,
        };
      },
      transformResponse: (response) => response?.data,
      invalidatesTags: ['TRANSACTION_TABLE_VALUES'],
    }),
    getCategories: builder.query<RiskCategories, string>({
      query: (map_name) => ({
        url: `/${map_name}/score-card`,
        method: 'get',
      }),
      transformResponse: (response) => response?.data,
      providesTags: ['SCORE_CARD'],
    }),
    modifyMapField: builder.mutation<
      any,
      {
        map_name: string;
        data: {
          map_metadata_id: string;
          value: any;
        };
      }
    >({
      query: ({ map_name, data }) => {
        return {
          url: `/${map_name}/score-card`,
          method: 'patch',
          data,
        };
      },
      invalidatesTags: ['SCORE_CARD', 'SCORE_CARD_PDF'],
    }),

    getMapOwnerContacts: builder.query<
      {
        id: string;
        name: string;
        email: string;
        departments?: string;
        groups?: string;
        label?: string;
      }[],
      { q?: string; role?: string }
    >({
      query: ({ q, role }) => {
        const params = new URLSearchParams();
        if (role) {
          params.set('role', role);
        }
        if (q) {
          params.set('q', q);
        }
        const search = params.toString();
        return {
          url: search ? `/owner-contacts?${search}` : '/owner-contacts',
          method: 'get',
        };
      },
      transformResponse: (response) => response?.data ?? [],
    }),
    updateMapOwners: builder.mutation<
      {
        sales_owner: string;
        sales_coordinator: string;
        client_service_specialist: string;
        operation_owner: string;
      },
      {
        map_name: string;
        sales_owner_contact_id?: string | null;
        sales_coordinator_contact_id?: string | null;
        client_service_specialist_contact_id?: string | null;
        operation_owner_contact_id?: string | null;
      }
    >({
      query: ({
        map_name,
        sales_owner_contact_id,
        sales_coordinator_contact_id,
        client_service_specialist_contact_id,
        operation_owner_contact_id,
      }) => ({
        url: `/${map_name}/owners`,
        method: 'patch',
        data: {
          ...(sales_owner_contact_id !== undefined
            ? { sales_owner_contact_id }
            : {}),
          ...(sales_coordinator_contact_id !== undefined
            ? { sales_coordinator_contact_id }
            : {}),
          ...(client_service_specialist_contact_id !== undefined
            ? { client_service_specialist_contact_id }
            : {}),
          ...(operation_owner_contact_id !== undefined
            ? { operation_owner_contact_id }
            : {}),
        },
      }),
      transformResponse: (response) => response?.data,
      invalidatesTags: ['MAP_LIST', 'SUMMARY_TABLE_VALUES'],
    }),

    getMapSummary: builder.query<MapSummary, string>({
      query: (map_name) => ({
        url: `/${map_name}`,
        method: 'get',
      }),
      transformResponse: (response) => response?.data,
      providesTags: ['SUMMARY_TABLE_VALUES'],
    }),
    getDealFactsheet: builder.query<DealFactsheetResponse, string>({
      query: (map_id) => ({
        url: `/${map_id}/deal-factsheet`,
        method: 'get',
      }),
      transformResponse: (response) => response?.data,
      providesTags: [
        'SUMMARY_TABLE_VALUES',
        'SCORE_CARD',
        'TABLE_VALUES',
        'TRANSACTION_TABLE_VALUES',
        'INSURERS',
        'ACCOUNT_COMMENTS',
      ],
    }),
    getMapHistorySummary: builder.query<MapSummary, string>({
      query: (map_name) => ({
        url: `/${map_name}/approvals/summary`,
        method: 'get',
      }),
      transformResponse: (response) => response?.data,
      providesTags: ['SUMMARY_TABLE_VALUES'],
    }),
    getInvestmentCard: builder.query<InvestmentCard, string>({
      query: (map_name) => ({
        url: `/${map_name}/investment-criteria-card`,
        method: 'get',
      }),
      transformResponse: (response) => response?.data,
    }),
    getScorecardHeader: builder.query<MapSummary, string>({
      query: (map_name) => ({
        url: `/${map_name}`,
        method: 'get',
      }),
      transformResponse: (response) => response?.data,
      providesTags: ['SCORECARD_HEADER'],
    }),
    editMapSummary: builder.mutation<
      any,
      { map_name: string; data: { [field_name: string]: any } }
    >({
      query: ({ map_name, data }) => {
        return {
          url: `/${map_name}`,
          method: 'patch',
          data,
        };
      },
      transformResponse: (response) => response?.data,
      invalidatesTags: ['SUMMARY_TABLE_VALUES', 'SCORE_CARD_PDF'],
    }),
    editScorecardHeader: builder.mutation<
      any,
      { map_name: any; data: { [field_name: string]: any } }
    >({
      query: ({ map_name, data }) => ({
        url: `/${map_name}`,
        method: 'patch',
        data,
      }),
      transformResponse: (response) => response?.data,
      invalidatesTags: ['SCORECARD_HEADER', 'SCORE_CARD_PDF'],
    }),
    getMapDocuments: builder.query<MapSummary, string>({
      query: (map_name) => ({
        url: `/${map_name}/documents`,
        method: 'get',
      }),
      transformResponse: (response) => response?.data,
      providesTags: ['MAP_DOCUMENTS'],
    }),
    getMapInsurers: builder.query<CreditLimit, string>({
      query: (map_name) => ({
        url: `/${map_name}/credit-limit`,
        method: 'get',
      }),
      transformResponse: (response) => response?.data,
      providesTags: ['INSURERS'],
    }),
    patchMapInsurer: builder.mutation<
      any,
      {
        credit_limit_id: string;
        data: Record<string, number>;
      }
    >({
      query: ({ credit_limit_id, data }) => ({
        url: `/credit-limits/${credit_limit_id}`,
        method: 'patch',
        data,
      }),
      invalidatesTags: ['INSURERS'],
    }),
    getAllInsurers: builder.query<
      Array<{ id: string; name: string; [key: string]: any }>,
      void
    >({
      query: () => ({
        url: `/insurers`,
        method: 'get',
      }),
      transformResponse: (response) => {
        if (Array.isArray(response)) return response;
        if (response?.data && Array.isArray(response.data))
          return response.data;
        return [];
      },
      providesTags: ['INSURERS'],
    }),
    getMapInsurersData: builder.query<
      {
        primary: Array<{
          id: string;
          insurer_id: string;
          insurer_name: string;
          [key: string]: any;
        }>;
        secondary: Array<{
          id: string;
          insurer_id: string;
          insurer_name: string;
          [key: string]: any;
        }>;
        topup: Array<{
          id: string;
          insurer_id: string;
          insurer_name: string;
          [key: string]: any;
        }>;
      },
      string
    >({
      query: (map_id) => ({
        url: `/operations/maps/${map_id}/insurers`,
        method: 'get',
      }),
      transformResponse: (response: any) => {
        const raw = response?.data ?? response ?? {};
        return {
          primary: Array.isArray(raw.primary) ? raw.primary : [],
          secondary: Array.isArray(raw.secondary) ? raw.secondary : [],
          topup: Array.isArray(raw.topup) ? raw.topup : [],
        };
      },
      providesTags: ['INSURERS'],
    }),
    getAccountComments: builder.query<
      AccountComment,
      { map_name: string; account_type: string }
    >({
      query: ({ map_name, account_type }) => ({
        url: `/${map_name}/${account_type}/summary`,
        method: 'get',
      }),
      transformResponse: (response) => response?.data,
      providesTags: ['ACCOUNT_COMMENTS'],
    }),
    getBuyerFinancialReportInsights: builder.query<
      BuyerFinancialReportInsightsResponse,
      string
    >({
      query: (map_name) => ({
        url: `/${map_name}/buyer/financial-report/insights`,
        method: 'get',
      }),
      keepUnusedDataFor: 600,
      transformResponse: (response) => response?.data,
    }),
    editAccountComments: builder.mutation<
      any,
      {
        map_name: string;
        account_type: string;
        data: {
          [field_name: string]: string;
        };
      }
    >({
      query: ({ map_name, account_type, data }) => ({
        url: `/${map_name}/${account_type}/summary`,
        data,
        method: 'put',
      }),
      transformResponse: (response) => response?.data,
      invalidatesTags: ['ACCOUNT_COMMENTS'],
    }),
    exportMap: builder.query<any, string>({
      query: (map_name) => ({
        url: `/${map_name}/score-card/export`,
        method: 'get',
      }),
      transformResponse: (response) => response,
      providesTags: ['SCORE_CARD_PDF'],
    }),
    getShareholdersHierarchy: builder.query<
      IShareholder,
      { map_name: string; account_type: string }
    >({
      query: ({ map_name, account_type }) => ({
        url: `/${map_name}/${account_type}/shareholders`,
        method: 'get',
      }),
      transformResponse: (response) => response?.data,
      providesTags: ['SHAREHOLDERS'],
    }),
    getEvaluationValues: builder.query<
      IEvaluation[],
      { map_name: string; account_type: string }
    >({
      query: ({ map_name, account_type }) => ({
        url: `/${map_name}/${account_type}/evaluation`,
        method: 'get',
      }),
      transformResponse: (response) => response?.data,
      providesTags: ['EVALUATION'],
    }),
    editEvaluation: builder.mutation<
      any,
      {
        map_name: string;
        account_type: 'supplier' | 'buyer';
        data: {
          map_metadata_id: string;
          source: string;
          value: any;
        };
      }
    >({
      query: ({ map_name, account_type, data }) => {
        return {
          url: `/${map_name}/${account_type}`,
          method: 'PATCH',
          data,
        };
      },
      transformResponse: (response) => response?.data,
      invalidatesTags: ['EVALUATION'],
    }),
    editTemplate: builder.mutation<
      any,
      {
        data: string[];
      }
    >({
      query: ({ data }) => ({
        url: `/template/field-order`,
        data,
        method: 'put',
      }),
      transformResponse: (response) => response?.data,
      invalidatesTags: ['TEMPLATES', 'SCORE_CARD'],
    }),
    getRiskMapApprovals: builder.query<Approval, string>({
      query: (map_name) => ({
        url: `/${map_name}/approval/risk`,
        method: 'get',
      }),
      transformResponse: (response) => response?.data,
      providesTags: ['RISK_MAP_APPROVERS'],
    }),
    approveRiskMap: builder.mutation<
      any,
      {
        map_name: string;
        data: ApproveMapModule;
      }
    >({
      query: ({ map_name, data }) => ({
        url: `/${map_name}/approval/risk`,
        method: 'put',
        data,
      }),
      transformResponse: (response) => response?.data,
      invalidatesTags: ['RISK_MAP_APPROVERS', 'SUMMARY_TABLE_VALUES'],
    }),
    editApproverRiskMap: builder.mutation<
      any,
      {
        map_name: string;
        data: Partial<ApproveMapModule>;
      }
    >({
      query: ({ map_name, data }) => ({
        url: `/${map_name}/approval/risk`,
        method: 'patch',
        data,
      }),
      transformResponse: (response) => response?.data,
      invalidatesTags: ['RISK_MAP_APPROVERS'],
    }),
    getLegalMapApprovals: builder.query<Approval, string>({
      query: (map_name) => ({
        url: `/${map_name}/approval/legal`,
        method: 'get',
      }),
      transformResponse: (response) => response?.data,
      providesTags: ['LEGAL_MAP_APPROVALS'],
    }),
    approveLegalMap: builder.mutation<
      any,
      {
        map_name: string;
        data: ApproveMapModule;
      }
    >({
      query: ({ map_name, data }) => ({
        url: `/${map_name}/approval/legal`,
        method: 'put',
        data,
      }),
      transformResponse: (response) => response?.data,
      invalidatesTags: ['LEGAL_MAP_APPROVALS', 'SUMMARY_TABLE_VALUES'],
    }),
    getComplianceMapApprovals: builder.query<
      ComplianceApprovals,
      { map_name: string; account_type: string }
    >({
      query: ({ map_name, account_type }) => ({
        url: `/${map_name}/${account_type}/compliance-approvals`,
        method: 'get',
      }),
      transformResponse: (response) => response?.data,
      providesTags: ['COMPLIANCE_MAP_APPROVERS'],
    }),
    editApprover: builder.mutation<
      any,
      {
        map_name: string;
        account_type: string;
        role: string;
        data: Partial<ApproveMapModule>;
      }
    >({
      query: ({ map_name, account_type, role, data }) => ({
        url: `/${map_name}/${account_type}/compliance-approvals/${role}`,
        method: 'patch',
        data,
      }),
      transformResponse: (response) => response?.data,
      invalidatesTags: ['COMPLIANCE_MAP_APPROVERS', 'SUMMARY_TABLE_VALUES'],
    }),
    approveComplianceMap: builder.mutation<
      any,
      {
        map_name: string;
        account_type: string;
        role: string;
        data: ApproveMapModule;
      }
    >({
      query: ({ map_name, account_type, role, data }) => ({
        url: `/${map_name}/${account_type}/compliance-approvals/${role}`,
        method: 'put',
        data,
      }),
      transformResponse: (response) => response?.data,
      invalidatesTags: ['COMPLIANCE_MAP_APPROVERS', 'SUMMARY_TABLE_VALUES'],
    }),

    getCreditCommitteeMapApprovals: builder.query<
      CreditCommitteeApprovals,
      string
    >({
      query: (map_name) => ({
        url: `/${map_name}/approval/credit-committee`,
        method: 'get',
      }),
      transformResponse: (response) => response?.data,
      providesTags: ['CREDIT_COMMITEE_APPROVERS'],
    }),

    approveCreditCommittee: builder.mutation<
      any,
      {
        map_name: string;
        role: string;
        data: ApproveMapModule;
      }
    >({
      query: ({ map_name, role, data }) => ({
        url: `/${map_name}/approval/credit-committee/${role}`,
        method: 'put',
        data,
      }),
      transformResponse: (response) => response?.data,
      invalidatesTags: ['CREDIT_COMMITEE_APPROVERS', 'SUMMARY_TABLE_VALUES'],
    }),

    getScreeningDocumentsShareholder: builder.query<
      any,
      { map_name: string; account_type: string; shareholder_id: string }
    >({
      query: ({ map_name, account_type, shareholder_id }) => ({
        url: `/${map_name}/${account_type}/screening/shareholders/${shareholder_id}/documents`,
        method: 'get',
      }),
      transformResponse: (response) => response?.data,
      providesTags: ['MAP_DOCUMENTS'],
    }),
    addScreeningDocumentShareholder: builder.mutation<
      any,
      {
        map_name: string;
        account_type: string;
        shareholder_id: string;
        data: FormData;
      }
    >({
      query: ({ map_name, account_type, shareholder_id, data }) => ({
        url: `/${map_name}/${account_type}/screening/shareholders/${shareholder_id}/documents`,
        method: 'POST',
        data,
      }),
      transformResponse: (response) => response?.data,
      invalidatesTags: ['MAP_DOCUMENTS'],
    }),
    getIncomlendProducts: builder.query<IncomlendProduct[], string | void>({
      query: (category_name) => ({
        url: `/incomlend-product${category_name != null && category_name !== '' ? `?category=${category_name}` : ''}`,
        method: 'get',
      }),
      transformResponse: (response) => response?.data,
    }),
    getIncomlendSubProducts: builder.query<
      IncomlendSubProduct[],
      string | void
    >({
      query: (category_name) => ({
        url: `/incomlend-subproduct${category_name != null && category_name !== '' ? `?category=${category_name}` : ''}`,
        method: 'get',
      }),
      transformResponse: (response) => response?.data,
    }),
    deleteMapDetailsBatch: builder.mutation<
      any,
      {
        map_name: string;
        account_type: 'supplier' | 'buyer';
        data: {
          map_metadata_id: string;
          source: string;
        }[];
      }
    >({
      query: ({ map_name, account_type, data }) => {
        return {
          url: `/${map_name}/${account_type}/batch`,
          method: 'DELETE',
          data,
        };
      },
      transformResponse: (response) => response?.data,
      invalidatesTags: ['TABLE_VALUES'],
      async onQueryStarted(arg, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          dispatch(maps.util.invalidateTags(['TABLE_VALUES']));
        } catch (error) {
          //eslint-disable-next-line no-console
          console.error('Error deleting director:', error);
        }
      },
    }),
    // origination tab endpoints
    getOriginationTab: builder.query<TradePresentation, string>({
      query: (map_name) => ({
        url: `/${map_name}/trade-presentation`,
        method: 'get',
      }),
      transformResponse: (response) => response?.data,
      providesTags: ['TRADE_PRESENTATION'],
    }),
    getCompanyLogo: builder.query<
      Logo,
      {
        map_name: string;
        account_type: string;
      }
    >({
      query: ({ map_name, account_type }) => ({
        url: `/${map_name}/${account_type}/company-logo`,
        method: 'get',
      }),
      transformResponse: (response) => response?.data,
    }),
    addCompanyLogo: builder.mutation<
      any,
      {
        map_name: string;
        account_type: string;
        file: File;
      }
    >({
      query: ({ map_name, account_type, file }) => {
        const data = new FormData();
        data.append('file', file);
        return {
          url: `/${map_name}/${account_type}/company-logo`,
          method: 'post',
          data,
        };
      },
      transformResponse: (response) => response?.data,
      invalidatesTags: ['TRADE_PRESENTATION'],
    }),
    editOriginationAccountComments: builder.mutation<
      any,
      {
        map_name: string;
        account_type: string;
        data: {
          [field_name: string]: string;
        };
      }
    >({
      query: ({ map_name, account_type, data }) => ({
        url: `/${map_name}/${account_type}/summary`,
        data,
        method: 'put',
      }),
      transformResponse: (response) => response?.data,
      invalidatesTags: ['TRADE_PRESENTATION'],
      async onQueryStarted(
        { map_name, account_type, data },
        { dispatch, queryFulfilled },
      ) {
        const patchResult = dispatch(
          maps.util.updateQueryData('getOriginationTab', map_name, (draft) => {
            if (draft.customer?.account_type === account_type) {
              Object.assign(draft.customer, data);
            } else if (draft.trade_partner?.account_type === account_type) {
              Object.assign(draft.trade_partner, data);
            }
          }),
        );
        try {
          await queryFulfilled;
        } catch {
          patchResult.undo();
        }
      },
    }),
    editOriginationTradeComments: builder.mutation<
      any,
      { map_name: string; data: { [field_name: string]: any } }
    >({
      query: ({ map_name, data }) => {
        return {
          url: `/${map_name}`,
          method: 'patch',
          data,
        };
      },
      transformResponse: (response) => response?.data,
      invalidatesTags: ['TRADE_PRESENTATION'],
      async onQueryStarted({ map_name, data }, { dispatch, queryFulfilled }) {
        const patchResult = dispatch(
          maps.util.updateQueryData('getOriginationTab', map_name, (draft) => {
            Object.assign(draft, data);
          }),
        );
        try {
          await queryFulfilled;
        } catch {
          patchResult.undo();
        }
      },
    }),
    GetMapPhaseStatus: builder.query<CreditCommitteeApprovals, string>({
      query: (map_name) => ({
        url: `/${map_name}/approvals/phase-done`,
        method: 'get',
      }),
      transformResponse: (response) => response?.data,
      providesTags: ['MAP_PHASE_STATUS'],
    }),
    MapFlowDecision: builder.mutation<
      any,
      {
        map_name: string;
        data: {
          decision: string;
          department: string;
          comment: string;
          phase: string;
        };
      }
    >({
      query: ({ map_name, data }) => {
        return {
          url: `/${map_name}/approve`,
          method: 'post',
          data,
        };
      },
      transformResponse: (response) => response?.data,
      invalidatesTags: [
        'MAP_LIST',
        'MAP_DOCUMENTS',
        'SUMMARY_TABLE_VALUES',
        'COMPLIANCE_MAP_APPROVERS',
        'RISK_MAP_APPROVERS',
        'LEGAL_MAP_APPROVALS',
        'CREDIT_COMMITEE_APPROVERS',
      ],
    }),
    MapResubmit: builder.mutation<
      any,
      {
        map_name: string;
        data: {
          comment: string;
          exception_authority: string;
        };
      }
    >({
      query: ({ map_name, data }) => ({
        url: `/${map_name}/resubmit`,
        method: 'post',
        data,
      }),
      transformResponse: (response) => response?.data,
      invalidatesTags: [
        'MAP_LIST',
        'MAP_DOCUMENTS',
        'SUMMARY_TABLE_VALUES',
        'MAP_PHASE_STATUS',
        'COMPLIANCE_MAP_APPROVERS',
        'RISK_MAP_APPROVERS',
        'LEGAL_MAP_APPROVALS',
        'CREDIT_COMMITEE_APPROVERS',
      ],
    }),
    editAccountWebsite: builder.mutation<
      any,
      { account_id: string; data: { website: string } }
    >({
      query: ({ account_id, data }) => {
        return {
          url: `/accounts/${account_id}`,
          method: 'patch',
          data,
        };
      },
      transformResponse: (response) => response?.data,
      invalidatesTags: ['TRADE_PRESENTATION'],
    }),
    getMapExtention: builder.query<any, string>({
      query: (account_relation_id) => ({
        url: `/map-extention/${account_relation_id}`,
        method: 'GET',
      }),
      transformResponse: (response) => response?.data,
      providesTags: ['MAP_EXTENTION'],
    }),
    createMapExtention: builder.mutation<
      any,
      {
        data: {
          account_id: string;
          map_extension_type: string;
          account_relateionships_id: string;
          internal_name: string;
        };
      }
    >({
      query: ({ data }) => ({
        url: `/map-extention`,
        method: 'POST',
        data,
      }),
      transformResponse: (response) => response?.data,
      invalidatesTags: ['MAP_EXTENTION'],
    }),
    deleteMapExtention: builder.mutation<
      any,
      { extension_id: string }
    >({
      query: ({ extension_id }) => ({
        url: `/map-extention/delete/${encodeURIComponent(extension_id)}`,
        method: 'DELETE',
      }),
      transformResponse: (response) => response?.data,
      invalidatesTags: ['MAP_EXTENTION'],
    }),
    getMapInstallment: builder.query<any, string>({
      query: (map_id) => ({
        url: `/map-installment`,
        method: 'GET',
        params: { map_id },
      }),
      transformResponse: (response) => response?.data,
      providesTags: ['MAP_INSTALLMENT'],
    }),
    createMapInstallment: builder.mutation<
      any,
      {
        data: {
          supplier_buyer_map_id: string;
          account_id: string;
          fee_type?: string;
          insurance_id?: string;
          fee_amount?: number;
          due_date?: string;
          comments?: string;
          postponed_date?: string;
          duration_period_months?: number;
          payment_status?: string;
          payment_date?: string;
        };
      }
    >({
      query: ({ data }) => ({
        url: `/map-installment`,
        method: 'POST',
        data,
      }),
      transformResponse: (response) => response?.data,
      invalidatesTags: ['MAP_INSTALLMENT'],
    }),
    updateMapInstallment: builder.mutation<
      any,
      { installment_id: string; data: Record<string, unknown> }
    >({
      query: ({ installment_id, data }) => ({
        url: `/map-installment/${installment_id}`,
        method: 'PATCH',
        data,
      }),
      transformResponse: (response) => response?.data,
      invalidatesTags: ['MAP_INSTALLMENT'],
    }),
    deleteMapInstallment: builder.mutation<any, { sf_map_id: string; internal_name: string }>({
      query: ({ sf_map_id, internal_name }) => ({
        url: `/map-installment`,
        method: 'DELETE',
        params: { sf_map_id, internal_name },
      }),
      transformResponse: (response) => response?.data,
      invalidatesTags: ['MAP_INSTALLMENT'],
    }),
    getMapInstallmentHistory: builder.query<any[], string>({
      query: (sf_map_id) => ({
        url: `/map-installment/history`,
        method: 'GET',
        params: { sf_map_id },
      }),
      transformResponse: (response) => response?.data,
      providesTags: ['MAP_INSTALLMENT'],
    }),
    getMapInstallmentById: builder.query<any, string>({
      query: (installment_id) => ({
        url: `/map-installment/${installment_id}`,
        method: 'GET',
      }),
      transformResponse: (response) => response?.data,
    }),
  }),
});

export const {
  useGetMapsQuery,
  // useGetMapQuery, no used
  useGetMapVersionQuery,
  useGetTemplatesQuery,
  useGetTemplateVersionQuery,
  useEditMapRiskParametersMutation,
  useGetMapRiskParametersQuery,
  useGetMapTransactionValuesQuery,
  useEditMapTransactionDetailsMutation,
  useGetCategoriesQuery,
  useModifyMapFieldMutation,
  useGetMapSummaryQuery,
  useUpdateMapOwnersMutation,
  useGetMapOwnerContactsQuery,
  useGetDealFactsheetQuery,
  useEditMapSummaryMutation,
  useGetMapDocumentsQuery,
  useGetAllInsurersQuery,
  useGetMapInsurersDataQuery,
  useGetMapInsurersQuery,
  usePatchMapInsurerMutation,
  useGetBuyerFinancialReportInsightsQuery,
  useGetAccountCommentsQuery,
  useEditAccountCommentsMutation,
  useGetScorecardHeaderQuery,
  useEditScorecardHeaderMutation,
  useExportMapQuery,
  useGetShareholdersHierarchyQuery,
  useGetEvaluationValuesQuery,
  useEditTemplateMutation,
  useGetRiskMapApprovalsQuery,
  useApproveRiskMapMutation,
  useEditApproverRiskMapMutation,
  useEditEvaluationMutation,
  useGetComplianceMapApprovalsQuery,
  useEditApproverMutation,
  useApproveComplianceMapMutation,
  useGetCreditCommitteeMapApprovalsQuery,
  useApproveCreditCommitteeMutation,
  useAddScreeningDocumentShareholderMutation,
  useGetScreeningDocumentsShareholderQuery,
  useGetLegalMapApprovalsQuery,
  useApproveLegalMapMutation,
  useGetIncomlendProductsQuery,
  useGetIncomlendSubProductsQuery,
  useDeleteMapDetailsBatchMutation,
  useGetTemplatesV2Query,
  useGetCurrentMapItemTemplatesQuery,
  useGetMapItemTemplatesQuery,
  useGetTemplateVersionsQuery,
  useGetMapItemTemplateByVersionQuery,
  useGetInvestmentCardQuery,
  useGetOriginationTabQuery,
  useEditOriginationAccountCommentsMutation,
  useEditOriginationTradeCommentsMutation,
  useEditAccountWebsiteMutation,
  useGetCompanyLogoQuery,
  useAddCompanyLogoMutation,
  useMapFlowDecisionMutation,
  useMapResubmitMutation,
  useGetMapPhaseStatusQuery,
  useGetMapHistorySummaryQuery,
  useCreateMapExtentionMutation,
  useDeleteMapExtentionMutation,
  useGetMapExtentionQuery,
  useGetMapInstallmentQuery,
  useCreateMapInstallmentMutation,
  useUpdateMapInstallmentMutation,
  useDeleteMapInstallmentMutation,
  useGetMapInstallmentHistoryQuery,
  useGetMapInstallmentByIdQuery,
} = maps;
